import { useState } from "react";
import toast from "react-hot-toast";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  useCreateSubjectArea,
  useDeleteSubjectArea,
  useReorderSubjectAreas,
  useSubjectAreas,
  useUpdateSubjectArea,
} from "../api/use-subject-areas";
import { SubjectAreaColorPicker } from "./subject-area-color-picker";
import type {
  SubjectArea,
  SubjectAreaCreateRequest,
  SubjectAreaUpdateRequest,
} from "../types";
import { SubjectAreaFormDialog } from "./subject-area-form-dialog";

interface EditSubjectAreasDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
}

type FormState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; area: SubjectArea };

export function EditSubjectAreasDialog({
  open,
  onClose,
  scheduleId,
}: EditSubjectAreasDialogProps) {
  // Only fetch when the dialog is actually open.
  const areasQuery = useSubjectAreas(scheduleId, { enabled: open });
  const createArea = useCreateSubjectArea(scheduleId);
  const updateArea = useUpdateSubjectArea(scheduleId);
  const deleteArea = useDeleteSubjectArea(scheduleId);
  const reorderAreas = useReorderSubjectAreas(scheduleId);

  const [form, setForm] = useState<FormState>({ kind: "closed" });
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * Id of the row whose color-swatch or reorder mutation is currently in
   * flight. Used to disable that row's controls only (the prior implementation
   * disabled every row's controls during any inflight mutation because the
   * `isPending` flag is shared across all rows).
   */
  const [pendingAreaId, setPendingAreaId] = useState<string | null>(null);

  const handleAdd = () => {
    setFormError(null);
    setForm({ kind: "create" });
  };

  const handleEdit = (area: SubjectArea) => {
    setFormError(null);
    setForm({ kind: "edit", area });
  };

  const handleCreateSubmit = async (data: SubjectAreaCreateRequest) => {
    setFormError(null);
    try {
      await createArea.mutateAsync(data);
      setForm({ kind: "closed" });
      toast.success("Subject area created");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to create subject area",
      );
    }
  };

  const handleUpdateSubmit = async (data: SubjectAreaUpdateRequest) => {
    if (form.kind !== "edit") return;
    setFormError(null);
    try {
      await updateArea.mutateAsync({
        areaId: form.area.id,
        payload: data,
      });
      setForm({ kind: "closed" });
      toast.success("Subject area updated");
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to update subject area",
      );
    }
  };

  const handleDelete = async (area: SubjectArea) => {
    const confirmed = window.confirm(
      `Delete subject area "${area.name}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await deleteArea.mutateAsync(area.id);
      toast.success("Subject area deleted");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete subject area",
      );
    }
  };

  const areas = areasQuery.data ?? [];

  const handlePickColor = async (area: SubjectArea, hex: string) => {
    if (hex.toUpperCase() === area.colorHex.toUpperCase()) return;
    setPendingAreaId(area.id);
    try {
      await updateArea.mutateAsync({
        areaId: area.id,
        payload: { name: area.name, colorHex: hex },
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update color",
      );
    } finally {
      setPendingAreaId((current) => (current === area.id ? null : current));
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= areas.length) return;
    const moving = areas[index];
    const next = [...areas];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setPendingAreaId(moving.id);
    try {
      await reorderAreas.mutateAsync(next.map((a) => a.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reorder");
    } finally {
      setPendingAreaId((current) => (current === moving.id ? null : current));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Subject area colors"
        description="Calendar events are colored by the Subject Area in their description."
        maxWidthClassName="max-w-2xl"
        testId="edit-subject-areas-dialog"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleAdd}
              data-testid="add-subject-area-button"
            >
              Add subject area
            </Button>
          </div>

          {areasQuery.isLoading && (
            <div className="py-8 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {areasQuery.isError && (
            <div
              role="alert"
              data-testid="subject-areas-load-error"
              className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
            >
              {areasQuery.error instanceof Error
                ? areasQuery.error.message
                : "Failed to load subject areas."}
            </div>
          )}

          {!areasQuery.isLoading &&
            !areasQuery.isError &&
            areas.length === 0 && (
              <div
                data-testid="subject-areas-empty"
                className="text-sm text-text-muted py-6 text-center"
              >
                No subject areas yet. Click “Add subject area” to create one.
              </div>
            )}

          {!areasQuery.isLoading && !areasQuery.isError && areas.length > 0 && (
            <ul className="divide-y divide-border-subtle/40">
              {areas.map((area, index) => (
                <SubjectAreaRow
                  key={area.id}
                  area={area}
                  isFirst={index === 0}
                  isLast={index === areas.length - 1}
                  isPending={pendingAreaId === area.id}
                  onMoveUp={() => handleMove(index, -1)}
                  onMoveDown={() => handleMove(index, 1)}
                  onEdit={() => handleEdit(area)}
                  onDelete={() => handleDelete(area)}
                  onPickColor={(hex) => handlePickColor(area, hex)}
                />
              ))}
            </ul>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              data-testid="close-button"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>

      {form.kind === "create" && (
        <SubjectAreaFormDialog
          open
          mode="create"
          onClose={() => setForm({ kind: "closed" })}
          onCreate={handleCreateSubmit}
          isLoading={createArea.isPending}
          error={formError}
        />
      )}

      {form.kind === "edit" && (
        <SubjectAreaFormDialog
          // Force a fresh mount per-area so local form state re-seeds when the
          // user opens edit on a different row without first closing the dialog.
          key={form.area.id}
          open
          mode="edit"
          area={form.area}
          freshArea={areas.find((a) => a.id === form.area.id) ?? form.area}
          onClose={() => setForm({ kind: "closed" })}
          onUpdate={handleUpdateSubmit}
          isLoading={updateArea.isPending}
          error={formError}
        />
      )}
    </>
  );
}

interface SubjectAreaRowProps {
  area: SubjectArea;
  isFirst: boolean;
  isLast: boolean;
  /** True while this row has an inflight color-pick or reorder mutation. */
  isPending: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPickColor: (hex: string) => void;
}

const ROW_ICON_BTN_BASE =
  "text-text-faint hover:text-text-soft disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface";

const ROW_ACTION_BTN_BASE =
  "text-text-faint text-sm rounded px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed";

function SubjectAreaRow({
  area,
  isFirst,
  isLast,
  isPending,
  onMoveUp,
  onMoveDown,
  onEdit,
  onDelete,
  onPickColor,
}: SubjectAreaRowProps) {
  return (
    <li
      data-testid={`subject-area-row-${area.id}`}
      aria-busy={isPending || undefined}
      className="flex items-center gap-3 py-3"
    >
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst || isPending}
          aria-label={`Move ${area.name} up`}
          data-testid={`move-up-subject-area-${area.id}`}
          className={ROW_ICON_BTN_BASE}
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast || isPending}
          aria-label={`Move ${area.name} down`}
          data-testid={`move-down-subject-area-${area.id}`}
          className={ROW_ICON_BTN_BASE}
        >
          ▼
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white truncate">
          {area.name}
        </div>
      </div>

      <div
        className={`flex-shrink-0 transition-opacity ${
          isPending ? "opacity-60" : ""
        }`}
        data-testid={`subject-area-row-swatches-${area.id}`}
      >
        <SubjectAreaColorPicker
          colorHex={area.colorHex}
          onChange={onPickColor}
          disabled={isPending}
          ariaLabel={`Color for ${area.name}`}
          testIdPrefix={`pick-color-${area.id}`}
          size="sm"
        />
      </div>

      <div className="inline-flex gap-3 flex-shrink-0 pl-2">
        <button
          type="button"
          onClick={onEdit}
          disabled={isPending}
          aria-label={`Edit subject area ${area.name}`}
          data-testid={`edit-subject-area-${area.id}`}
          className={`${ROW_ACTION_BTN_BASE} hover:text-text-soft`}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={isPending}
          data-testid={`delete-subject-area-${area.id}`}
          aria-label={`Delete subject area ${area.name}`}
          className={`${ROW_ACTION_BTN_BASE} hover:text-red-400`}
        >
          Delete
        </button>
      </div>
    </li>
  );
}
